import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Users2, ShieldAlert, Copy, RefreshCcw, Trash2, Plus } from "lucide-react";

const ROLES = ["admin", "member", "viewer"];

export default function SCIMSettings() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [tokenStatus, setTokenStatus] = useState(undefined);
  const [newToken, setNewToken] = useState(null);
  const [config, setConfig] = useState({ default_role: "member" });
  const [mappings, setMappings] = useState([]);
  const [mappingForm, setMappingForm] = useState({ group_display_name: "", role: "member" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tRes, cRes, mRes] = await Promise.all([
        api.get("/scim/token/status"),
        api.get("/scim/config"),
        api.get("/scim/role-mappings"),
      ]);
      setTokenStatus(tRes.data || null);
      setConfig(cRes.data || { default_role: "member" });
      setMappings(mRes.data || []);
    } catch {
      toast.error("Failed to load SCIM settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const issueToken = async () => {
    if (tokenStatus && !window.confirm("Generating a new token invalidates the current one — your IdP's SCIM connector will need updating. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scim/token");
      setNewToken(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate token");
    } finally {
      setBusy(false);
    }
  };

  const revokeToken = async () => {
    if (!window.confirm("Revoke the SCIM token? Your IdP will no longer be able to sync users until you generate a new one.")) return;
    try {
      await api.delete("/scim/token");
      toast.success("Token revoked");
      setNewToken(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const saveDefaultRole = async (role) => {
    try {
      await api.put("/scim/config", { default_role: role });
      setConfig(c => ({ ...c, default_role: role }));
      toast.success("Default role updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const addMapping = async () => {
    if (!mappingForm.group_display_name.trim()) return toast.error("Group name is required");
    try {
      await api.put("/scim/role-mappings", mappingForm);
      toast.success("Mapping saved");
      setMappingForm({ group_display_name: "", role: "member" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save mapping");
    }
  };

  const removeMapping = async (groupName) => {
    try {
      await api.delete(`/scim/role-mappings/${encodeURIComponent(groupName)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove mapping");
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(newToken.token);
    toast.success("Token copied");
  };

  const scimBaseUrl = `${window.location.origin}/api/scim/v2`;

  if (!isManager) {
    return (
      <div className="p-10 text-center text-sm text-[#7d8590] flex flex-col items-center gap-2">
        <ShieldAlert className="w-6 h-6" />
        Only workspace owners and admins can manage SCIM provisioning.
      </div>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users2 className="w-5 h-5 text-[#7d8590]" /> SCIM Provisioning</h1>
        <p className="text-sm text-[#7d8590] mt-1">Let your identity provider automatically create, update and deactivate accounts here — no manual invites needed.</p>
      </div>

      {newToken && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="text-sm text-amber-300 font-medium mb-2">Copy this token now — it won't be shown again.</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-[#161b22] border border-[#30363d] rounded-md px-3 py-2 text-xs text-white truncate">{newToken.token}</code>
            <button onClick={copyToken} className="text-amber-300 hover:text-white"><Copy className="w-4 h-4" /></button>
          </div>
          <div className="text-xs text-[#7d8590] mt-2">SCIM base URL: <code className="text-[#9CA3AF]">{scimBaseUrl}</code></div>
        </div>
      )}

      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-semibold">Connector token</div>
          {tokenStatus && <span className="text-xs px-2 py-0.5 rounded-full border text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20">configured</span>}
        </div>
        {tokenStatus ? (
          <div className="text-xs text-[#7d8590] mb-4">
            Ends in <code className="text-[#9CA3AF]">…{tokenStatus.token_preview}</code> · created {new Date(tokenStatus.created_at).toLocaleDateString()}
            {tokenStatus.last_used_at && <> · last used {new Date(tokenStatus.last_used_at).toLocaleString()}</>}
          </div>
        ) : (
          <div className="text-xs text-[#7d8590] mb-4">No token generated yet — your IdP can't sync users until you create one.</div>
        )}
        <div className="flex gap-2">
          <Button onClick={issueToken} disabled={busy} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <RefreshCcw className="w-4 h-4 mr-1" /> {tokenStatus ? "Regenerate" : "Generate"} token
          </Button>
          {tokenStatus && (
            <Button onClick={revokeToken} variant="outline" className="border-rose-500/20 text-rose-400 hover:bg-rose-500/10">
              <Trash2 className="w-4 h-4 mr-1" /> Revoke
            </Button>
          )}
        </div>
      </div>

      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5">
        <div className="text-white font-semibold mb-3">New members join as</div>
        <select value={config.default_role} onChange={e => saveDefaultRole(e.target.value)}
          className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
          <option value="member">Member</option>
          <option value="viewer">Viewer</option>
        </select>
        <p className="text-[11px] text-[#7d8590] mt-2">Applies unless a group role mapping below promotes them.</p>
      </div>

      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5">
        <div className="text-white font-semibold mb-1">Group role mapping</div>
        <p className="text-xs text-[#7d8590] mb-3">When your IdP pushes a group with this exact name, its members get this role. Removing someone from the group does not automatically demote them.</p>
        <div className="space-y-2 mb-4">
          {mappings.map(m => (
            <div key={m.group_display_name} className="flex items-center justify-between bg-[#161b22] border border-[#30363d] rounded-md px-3 py-2">
              <div className="text-sm text-white">{m.group_display_name} <span className="text-[#7d8590]">→</span> <span className="capitalize text-[#00F5D4]">{m.role}</span></div>
              <button onClick={() => removeMapping(m.group_display_name)} className="text-[#484f58] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {mappings.length === 0 && <div className="text-xs text-[#484f58]">No group mappings configured.</div>}
        </div>
        <div className="flex gap-2">
          <input value={mappingForm.group_display_name} onChange={e => setMappingForm(f => ({ ...f, group_display_name: e.target.value }))}
            placeholder="Group name, e.g. Admins"
            className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
          <select value={mappingForm.role} onChange={e => setMappingForm(f => ({ ...f, role: e.target.value }))}
            className="bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <Button onClick={addMapping} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div></DarkPage>
  );
}
